import { useMemo, useState } from 'react';
import { scr } from '../../lib/api';
import { MDA_OPTIONS, mdaName } from '../../lib/constants';
import type { BudgetLinesResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatCard } from '../../ui/components';

const YEARS = [2026, 2025];

export function Budget() {
  const [year, setYear] = useState('2026');
  const [mda, setMda] = useState('');
  const [programme, setProgramme] = useState('');

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (year) p.set('year', year);
    if (mda) p.set('mda', mda);
    if (programme.trim()) p.set('programme', programme.trim());
    return `/api/v1/opengov/budget-lines?${p}`;
  }, [year, mda, programme]);
  const q = useApiQuery<BudgetLinesResponse>(query);

  const rows = q.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Budget Explorer"
        subtitle="Allocated, revised and spent amounts by ministry/department and programme, consolidated from the national budget."
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="field-label">Fiscal year</span>
          <select className="field-input" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">All years</option>
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="field-label">Ministry / department</span>
          <select className="field-input min-w-[220px]" value={mda} onChange={(e) => setMda(e.target.value)}>
            <option value="">All</option>
            {MDA_OPTIONS.map((m) => (
              <option key={m.code} value={m.code}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="field-label">Programme (exact name)</span>
          <input
            className="field-input min-w-[220px]"
            value={programme}
            onChange={(e) => setProgramme(e.target.value)}
            placeholder="e.g. Primary Healthcare Services"
          />
        </label>
      </div>

      {q.data ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-3">
          <StatCard label="Allocated" value={scr(q.data.totals.allocated)} hint="Original budget, matching filters" />
          <StatCard label="Revised" value={scr(q.data.totals.revised)} hint="After in-year adjustments" tone="warn" />
          <StatCard label="Spent" value={scr(q.data.totals.spent)} hint={`${rows.length} budget line(s)`} tone="good" />
        </div>
      ) : null}

      {q.error ? <ErrorNote message={q.error} /> : null}
      <div className="card overflow-x-auto">
        {q.loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState title="No budget lines match these filters" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Year</th>
                <th className="table-head">Ministry / department</th>
                <th className="table-head">Programme</th>
                <th className="table-head text-right">Allocated</th>
                <th className="table-head text-right">Revised</th>
                <th className="table-head text-right">Spent</th>
                <th className="table-head w-40">Execution</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const revised = Number.parseFloat(String(r.revised));
                const spent = Number.parseFloat(String(r.spent));
                const pct = revised > 0 ? Math.min(100, Math.round((spent / revised) * 100)) : 0;
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="table-cell tabular-nums">{r.year}</td>
                    <td className="table-cell font-medium" title={mdaName(r.mda)}>
                      {r.mda}
                    </td>
                    <td className="table-cell">{r.programme}</td>
                    <td className="table-cell text-right tabular-nums">{scr(r.allocated)}</td>
                    <td className="table-cell text-right tabular-nums">{scr(r.revised)}</td>
                    <td className="table-cell text-right tabular-nums">{scr(r.spent)}</td>
                    <td className="table-cell">
                      <div className="h-2 w-full rounded-full bg-slate-100">
                        <div
                          className={`h-2 rounded-full ${pct > 95 ? 'bg-rose-500' : pct > 75 ? 'bg-amber-500' : 'bg-brand-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-400">{pct}% of revised spent</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {q.data?.nextCursor ? (
        <p className="mt-2 text-xs text-slate-400">More rows are available — narrow the filters above to see them.</p>
      ) : null}
    </div>
  );
}
